import {
  Alert,
  App as AntdApp,
  Card,
  Empty,
  Flex,
  Switch,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { Wrench } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { WorkspaceAgent } from 'src/api/facade/types'
import {
  ContentSpinner,
  MonoValue,
  QueryErrorAlert,
  SectionLabel
} from 'src/components'
import type {
  AgentToolWrite,
  SquadToolCatalogEntry
} from 'src/services/agentTools'
import { useShowApiErrors } from 'src/utils'

import {
  useAgentTools,
  useSetAgentTools,
  useToolCatalog
} from './useAgentTools'

const { Text } = Typography

type Props = {
  workspaceId: number
  agent: WorkspaceAgent
}

/**
 * Connections tab — the agent's built-in function tools (`/api/tools/` catalog,
 * `/api/agents/{id}/tools/` per-agent set). Toggle a tool on/off; the full set
 * is saved with a replace-all PUT. Distinct from the MCP tab (remote servers).
 */
export const AgentToolsTab = ({ workspaceId, agent }: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const showApiErrors = useShowApiErrors()
  const readOnly = agent.canEdit === false

  const catalog = useToolCatalog(workspaceId)
  const agentTools = useAgentTools(workspaceId, agent.id)
  const setMut = useSetAgentTools(workspaceId, agent.id)

  const isLoading = catalog.isLoading || agentTools.isLoading
  const isError = catalog.isError || agentTools.isError

  // Current per-agent set keyed by tool_key (carries each tool's config).
  const enabled = new Map(
    (agentTools.data ?? []).map((tool) => [tool.tool_key, tool.config])
  )

  const toggle = (key: string, on: boolean) => {
    const next: AgentToolWrite[] = (agentTools.data ?? [])
      .filter((tool) => tool.tool_key !== key)
      .map((tool) => ({ tool_key: tool.tool_key, config: tool.config }))
    if (on) next.push({ tool_key: key, config: enabled.get(key) ?? {} })

    setMut.mutate(next, {
      onSuccess: () =>
        message.success(t('workspace.agents.builtinTools.updated')),
      onError: (e) => showApiErrors(e)
    })
  }

  // Native built-in tools only — provider-backed ones (e.g. Firecrawl) live on
  // the External tools tab. Group by category, preserving catalog order.
  const groups: { category: string; tools: SquadToolCatalogEntry[] }[] = []
  for (const entry of catalog.data ?? []) {
    if (entry.provider) continue
    const category =
      entry.category || t('workspace.agents.builtinTools.otherCategory')
    const group = groups.find((g) => g.category === category)
    if (group) group.tools.push(entry)
    else groups.push({ category, tools: [entry] })
  }

  const renderTool = (tool: SquadToolCatalogEntry) => {
    const isOn = enabled.has(tool.key)

    return (
      <Flex
        key={tool.key}
        align="flex-start"
        justify="space-between"
        gap={12}
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid var(--ant-color-fill-tertiary)'
        }}
      >
        <Flex align="flex-start" gap={10} style={{ minWidth: 0 }}>
          <span
            style={{
              width: 26,
              height: 26,
              borderRadius: 6,
              background: 'var(--ant-color-bg-container)',
              border: '1px solid var(--ant-color-border-secondary)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 auto',
              marginTop: 2,
              color: 'var(--ant-color-text-secondary)'
            }}
          >
            {tool.provider?.icon_url ? (
              <img
                src={tool.provider.icon_url}
                alt=""
                width={16}
                height={16}
                style={{ borderRadius: 3 }}
              />
            ) : (
              <Wrench size={14} />
            )}
          </span>
          <Flex vertical style={{ minWidth: 0 }} gap={2}>
            <Flex align="center" gap={6} wrap="wrap">
              <Text strong style={{ fontSize: 13 }}>
                {tool.name}
              </Text>
              <MonoValue copyable={false} fontSize={11}>
                {tool.key}
              </MonoValue>
              {tool.provider ? (
                <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>
                  {tool.provider.name}
                </Tag>
              ) : null}
            </Flex>
            {tool.description ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {tool.description}
              </Text>
            ) : null}
          </Flex>
        </Flex>
        <Tooltip
          title={
            isOn
              ? t('workspace.agents.builtinTools.enabledTip')
              : t('workspace.agents.builtinTools.disabledTip')
          }
        >
          <Switch
            size="small"
            checked={isOn}
            disabled={readOnly || setMut.isPending}
            onChange={(v) => toggle(tool.key, v)}
            style={{ flex: '0 0 auto' }}
          />
        </Tooltip>
      </Flex>
    )
  }

  return (
    <Flex vertical gap={14}>
      <Alert
        type="info"
        showIcon
        message={t('workspace.agents.builtinTools.introTitle')}
        description={t('workspace.agents.builtinTools.introBody')}
      />

      <Card
        title={t('workspace.agents.builtinTools.title')}
        styles={{ body: { padding: 0 } }}
      >
        {isError ? (
          <div style={{ padding: 14 }}>
            <QueryErrorAlert
              onRetry={() => {
                void catalog.refetch()
                void agentTools.refetch()
              }}
            />
          </div>
        ) : isLoading ? (
          <ContentSpinner minHeight={160} />
        ) : groups.length === 0 ? (
          <div style={{ padding: 24 }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('workspace.agents.builtinTools.empty')}
                </Text>
              }
            />
          </div>
        ) : (
          <div>
            {groups.map((group) => (
              <div key={group.category}>
                <div
                  style={{
                    padding: '8px 12px',
                    background: 'var(--ant-color-fill-tertiary)',
                    borderBottom: '1px solid var(--ant-color-border-secondary)'
                  }}
                >
                  <SectionLabel>{group.category}</SectionLabel>
                </div>
                {group.tools.map(renderTool)}
              </div>
            ))}
          </div>
        )}
      </Card>
    </Flex>
  )
}
